/**
 * SearchTermResultsDialog Component
 *
 * Modal dialog that displays live store search results for a given term.
 * Accepts an `onSearch` callback to fetch results, decoupling the component
 * from any specific API client or auth provider.
 */

import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
} from "@a7y/tailwind-catalyst";
import {
  ArrowTopRightOnSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import type { StoreSearchResultItem } from "@a7y/types";

export interface SearchTermResultsDialogProps {
  open: boolean;
  onClose: () => void;
  term: string;
  storefront: string;
  countryCode: string;
  languageCode?: string;
  /** Callback to fetch search results. Must return the array of result items. */
  onSearch?: (params: {
    term: string;
    storefront: string;
    countryCode: string;
    languageCode?: string;
  }) => Promise<StoreSearchResultItem[]>;
}

export function SearchTermResultsDialog({
  open,
  onClose,
  term,
  storefront,
  countryCode,
  languageCode,
  onSearch,
}: SearchTermResultsDialogProps) {
  const [results, setResults] = useState<StoreSearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResults = useCallback(async () => {
    if (!term || !onSearch) return;
    setLoading(true);
    setError(null);
    try {
      const items = await onSearch({ term, storefront, countryCode, languageCode });
      setResults(items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch search results");
    } finally {
      setLoading(false);
    }
  }, [term, storefront, countryCode, languageCode, onSearch]);

  useEffect(() => {
    if (open) {
      setResults([]);
      fetchResults();
    }
  }, [open, fetchResults]);

  return (
    <Dialog open={open} onClose={onClose} size="3xl" className="max-h-[80vh] flex flex-col" centered>
      <div className="flex items-center justify-between">
        <DialogTitle>Store results for &ldquo;{term}&rdquo;</DialogTitle>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-zinc-800 rounded-lg transition-colors -mt-2 -mr-2"
        >
          <XMarkIcon className="size-5" />
        </button>
      </div>

      <DialogBody className="overflow-y-auto min-h-0">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            <Button outline onClick={fetchResults}>
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && results.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">No results found</p>
          </div>
        )}

        {/* Results table */}
        {!loading && !error && results.length > 0 && (
          <div className="overflow-x-hidden [&>div>div]:whitespace-normal [&>div>div]:overflow-x-hidden [&>div>div>div]:block [&_table]:table-fixed [&_table]:w-full">
            <Table dense>
              <TableHead>
                <TableRow>
                  <TableHeader className="w-10">#</TableHeader>
                  <TableHeader>App</TableHeader>
                  <TableHeader className="w-36">Store Link</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((item, index) => (
                  <TableRow key={item.appId}>
                    <TableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {item.iconUrl && (
                          <img
                            src={item.iconUrl}
                            alt=""
                            className="h-8 w-8 rounded-lg shrink-0"
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white break-words">
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 break-words">
                              {item.subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.storeUrl ? (
                        <a
                          href={item.storeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          View in Store
                          <ArrowTopRightOnSquareIcon className="size-3" />
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogBody>

    </Dialog>
  );
}
